/**
 * MediaLibraryManager Component
 *
 * Full admin page for managing the media library.
 * Features:
 * - Grid/list view of all media
 * - Search and filter by tags
 * - Bulk operations (tag, delete)
 * - Usage detection before deletion
 * - Upload new media
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  Upload,
  Loader2,
  Image as ImageIcon,
  Check,
  Filter,
  RefreshCw,
  LayoutGrid,
  List,
  Trash2,
  Tag,
  Eye,
  EyeOff,
  MoreVertical,
  AlertTriangle,
  Info,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import { ConfirmDialog, useConfirmDialog, PageTitle } from './shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { media, uploads } from '@/services/api/admin';


const MediaLibraryManager = () => {
  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [visibilityFilter, setVisibilityFilter] = useState('all'); // 'all', 'visible', 'hidden'
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Dialog states
  const [editItem, setEditItem] = useState(null);
  const [usageDialog, setUsageDialog] = useState(null);
  const [bulkTagDialog, setBulkTagDialog] = useState(false);
  const [newTags, setNewTags] = useState('');

  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const { confirmProps, confirm } = useConfirmDialog();

  const PAGE_SIZE = 24;

  // Fetch media items
  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page,
        pageSize: PAGE_SIZE,
        sortBy,
        sortOrder,
      };

      if (search) params.search = search;
      if (selectedTags.length > 0) params.tags = selectedTags.join(',');
      if (visibilityFilter === 'visible') params.inGallery = true;
      if (visibilityFilter === 'hidden') params.inGallery = false;

      const result = await media.list(params);
      setItems(result.items);
      setTotalPages(result.total_pages);
      setTotal(result.total);
    } catch (err) {
      setError(err.message || 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedTags, visibilityFilter, sortBy, sortOrder]);

  // Fetch all tags
  const fetchTags = useCallback(async () => {
    try {
      const tags = await media.getTags();
      setAllTags(tags);
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchMedia();
    fetchTags();
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchMedia();
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Refetch when filters change
  useEffect(() => {
    setPage(1);
    fetchMedia();
  }, [selectedTags, visibilityFilter, sortBy, sortOrder]);

  // Refetch on page change
  useEffect(() => {
    fetchMedia();
  }, [page]);

  // Handle file upload
  const handleUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError('');
    setSuccess('');

    let uploadedCount = 0;
    let failedCount = 0;

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        failedCount++;
        continue;
      }

      if (file.size > 5 * 1024 * 1024) {
        failedCount++;
        continue;
      }

      try {
        await uploads.uploadImage(file, { autoGallery: true });
        uploadedCount++;
      } catch (err) {
        failedCount++;
        console.error('Failed to upload:', file.name, err);
      }
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (uploadedCount > 0) {
      setSuccess(`Uploaded ${uploadedCount} image(s)${failedCount > 0 ? `, ${failedCount} failed` : ''}`);
      fetchMedia();
      fetchTags();
    } else if (failedCount > 0) {
      setError(`Failed to upload ${failedCount} image(s)`);
    }

    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3000);
  };

  // Toggle item selection
  const toggleSelection = (id) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Select all / deselect all
  const toggleSelectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(item => item.id)));
    }
  };

  // Check usage before delete
  const handleDelete = async (item) => {
    try {
      const usage = await media.getUsage(item.id);

      if (usage.usages.length > 0) {
        setUsageDialog({
          item,
          usages: usage.usages,
        });
      } else {
        const confirmed = await confirm({
          title: 'Delete Image',
          message: `Are you sure you want to delete "${item.title}"? This cannot be undone.`,
          confirmLabel: 'Delete',
          variant: 'destructive',
        });

        if (confirmed) {
          await media.delete(item.id);
          setSuccess('Image deleted successfully');
          fetchMedia();
          setTimeout(() => setSuccess(''), 3000);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to delete image');
    }
  };

  // Force delete with usage warning
  const handleForceDelete = async () => {
    if (!usageDialog) return;

    try {
      await media.delete(usageDialog.item.id, true);
      setSuccess('Image deleted (references may be broken)');
      setUsageDialog(null);
      fetchMedia();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete image');
    }
  };

  // Toggle gallery inclusion (whether it appears in Gallery Editor)
  const handleToggleGallery = async (item) => {
    try {
      await media.update(item.id, { in_gallery: !item.in_gallery });
      fetchMedia();
    } catch (err) {
      setError(err.message || 'Failed to update');
    }
  };

  // Update item
  const handleUpdateItem = async () => {
    if (!editItem) return;

    try {
      await media.update(editItem.id, {
        title: editItem.title,
        description: editItem.description,
        in_gallery: editItem.in_gallery,
      });
      setSuccess('Updated successfully');
      setEditItem(null);
      fetchMedia();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update');
    }
  };

  // Bulk tag update
  const handleBulkTagUpdate = async (mode) => {
    if (selectedItems.size === 0) return;

    const tagList = newTags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
    if (tagList.length === 0) return;

    try {
      if (mode === 'add') {
        await media.bulkUpdateTags(Array.from(selectedItems), tagList, []);
      } else {
        await media.bulkUpdateTags(Array.from(selectedItems), [], tagList);
      }

      setSuccess(`Tags ${mode === 'add' ? 'added to' : 'removed from'} ${selectedItems.size} items`);
      setBulkTagDialog(false);
      setNewTags('');
      setSelectedItems(new Set());
      fetchMedia();
      fetchTags();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update tags');
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;

    const confirmed = await confirm({
      title: 'Delete Selected Images',
      message: `Are you sure you want to delete ${selectedItems.size} image(s)? Items in use will be skipped.`,
      confirmLabel: 'Delete',
      variant: 'destructive',
    });

    if (!confirmed) return;

    try {
      const result = await media.bulkDelete(Array.from(selectedItems), false);
      const message = `Deleted ${result.deleted_count} item(s)${result.skipped_count > 0 ? `, ${result.skipped_count} skipped (in use)` : ''}`;
      setSuccess(message);
      setSelectedItems(new Set());
      fetchMedia();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete');
    }
  };

  // Bulk add/remove from gallery
  const handleBulkGalleryUpdate = async (addToGallery) => {
    if (selectedItems.size === 0) return;

    try {
      let updatedCount = 0;
      for (const itemId of selectedItems) {
        await media.update(itemId, { in_gallery: addToGallery });
        updatedCount++;
      }

      setSuccess(`${addToGallery ? 'Added' : 'Removed'} ${updatedCount} item(s) ${addToGallery ? 'to' : 'from'} Gallery`);
      setSelectedItems(new Set());
      fetchMedia();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update gallery status');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-4">
      <PageTitle
        title="Media Library"
        description="Manage all uploaded images, organize with tags, and track usage"
        icon={<ImageIcon className="h-6 w-6" />}
      />

      {/* Header Controls */}
      <div className="flex flex-col gap-3">
        {/* Search & Actions Row */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title or filename..."
              className="pl-10"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-stone-100' : ''}
          >
            <Filter className="h-4 w-4 mr-1.5" />
            Filters
            {(selectedTags.length > 0 || visibilityFilter !== 'all') && (
              <Badge variant="secondary" className="ml-1.5">
                {selectedTags.length + (visibilityFilter !== 'all' ? 1 : 0)}
              </Badge>
            )}
          </Button>

          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-r-none"
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-l-none"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Upload className="h-4 w-4 mr-1.5" />
                Upload
              </>
            )}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => { fetchMedia(); fetchTags(); }}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            className="hidden"
          />
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-4 p-3 bg-stone-50 rounded-lg border">
            {/* Gallery filter */}
            <div className="flex items-center gap-2">
              <Label className="text-sm">Gallery:</Label>
              <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
                <SelectTrigger className="w-[150px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Images</SelectItem>
                  <SelectItem value="visible">In Gallery</SelectItem>
                  <SelectItem value="hidden">Not in Gallery</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <Label className="text-sm">Sort:</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[130px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Date Added</SelectItem>
                  <SelectItem value="title">Title</SelectItem>
                  <SelectItem value="file_size_bytes">Size</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                className="h-8 px-2"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </Button>
            </div>

            {/* Tags */}
            {allTags.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-sm">Tags:</Label>
                {allTags.slice(0, 8).map(({ tag, count }) => (
                  <Badge
                    key={tag}
                    variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      setSelectedTags(prev =>
                        prev.includes(tag)
                          ? prev.filter(t => t !== tag)
                          : [...prev, tag]
                      );
                    }}
                  >
                    {tag} ({count})
                  </Badge>
                ))}
              </div>
            )}

            {(selectedTags.length > 0 || visibilityFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedTags([]);
                  setVisibilityFilter('all');
                }}
                className="text-xs"
              >
                Clear filters
              </Button>
            )}
          </div>
        )}

        {/* Selection actions */}
        {selectedItems.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <span className="text-sm font-medium text-amber-800">
              {selectedItems.size} selected
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkGalleryUpdate(true)}
              className="text-green-600 hover:text-green-700"
            >
              <Eye className="h-4 w-4 mr-1" />
              Add to Gallery
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkGalleryUpdate(false)}
            >
              <EyeOff className="h-4 w-4 mr-1" />
              Remove from Gallery
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkTagDialog(true)}
            >
              <Tag className="h-4 w-4 mr-1" />
              Manage Tags
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkDelete}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedItems(new Set())}
            >
              Clear selection
            </Button>
          </div>
        )}
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <Check className="h-4 w-4" />
          {success}
        </div>
      )}

      {/* Stats bar */}
      <div className="flex items-center justify-between text-sm text-stone-500 dark:text-stone-400">
        <span>{total} total items</span>
        {items.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSelectAll}
            className="h-7 text-xs"
          >
            {selectedItems.size === items.length ? 'Deselect all' : 'Select all'}
          </Button>
        )}
      </div>

      {/* Media Grid/List */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-stone-500 dark:text-stone-400">
          <ImageIcon className="h-16 w-16 mb-4 text-stone-300" />
          <p className="text-lg font-medium">No images found</p>
          <p className="text-sm">Upload images or adjust your filters</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
          {items.map((item) => (
            <div
              key={item.id}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all group
                ${selectedItems.has(item.id)
                  ? 'border-amber-500 ring-2 ring-amber-200'
                  : 'border-stone-200 hover:border-stone-400'
                }
                ${!item.in_gallery ? 'opacity-80' : ''}
              `}
            >
              {/* Main image - click to open detail modal */}
              <div
                className="w-full h-full cursor-pointer"
                onClick={() => setEditItem(item)}
              >
                <img
                  src={item.media_url}
                  alt={item.title}
                  className="w-full h-full object-cover object-top bg-stone-100"
                  loading="lazy"
                />
              </div>

              {/* Selection checkbox */}
              <div className="absolute top-2 left-2 z-10">
                <Checkbox
                  checked={selectedItems.has(item.id)}
                  className="bg-background/80"
                  onClick={(e) => e.stopPropagation()}
                  onCheckedChange={() => toggleSelection(item.id)}
                />
              </div>

              {/* Not in Gallery indicator */}
              {!item.in_gallery && (
                <div className="absolute top-2 right-2 bg-black/60 text-white rounded p-0.5" title="Not in Gallery Editor">
                  <EyeOff className="h-3 w-3" />
                </div>
              )}

              {/* Actions menu */}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center justify-between">
                  <span className="text-white text-xs truncate flex-1 mr-2">{item.title}</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-white hover:bg-background/20">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditItem(item); }}>
                        <Info className="h-4 w-4 mr-2" />
                        Edit Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleToggleGallery(item); }}>
                        {item.in_gallery ? (
                          <>
                            <EyeOff className="h-4 w-4 mr-2" />
                            Remove from Gallery
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-2" />
                            Add to Gallery
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b">
              <tr>
                <th className="w-10 p-2">
                  <Checkbox
                    checked={selectedItems.size === items.length && items.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </th>
                <th className="w-16 p-2"></th>
                <th className="text-left p-2">Title</th>
                <th className="text-left p-2 hidden md:table-cell">Tags</th>
                <th className="text-left p-2 hidden sm:table-cell">Size</th>
                <th className="text-left p-2 hidden lg:table-cell">Added</th>
                <th className="text-center p-2">In Gallery</th>
                <th className="w-10 p-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className={`border-b hover:bg-stone-50 ${selectedItems.has(item.id) ? 'bg-amber-50' : ''}`}
                >
                  <td className="p-2">
                    <Checkbox
                      checked={selectedItems.has(item.id)}
                      onCheckedChange={() => toggleSelection(item.id)}
                    />
                  </td>
                  <td className="p-2">
                    <img
                      src={item.media_url}
                      alt={item.title}
                      className="h-10 w-14 object-cover rounded bg-stone-100"
                    />
                  </td>
                  <td className="p-2">
                    <div className="font-medium truncate max-w-[200px]">{item.title}</div>
                    {item.original_filename && (
                      <div className="text-xs text-stone-400 truncate max-w-[200px]">
                        {item.original_filename}
                      </div>
                    )}
                  </td>
                  <td className="p-2 hidden md:table-cell">
                    <div className="flex gap-1 flex-wrap">
                      {item.tags?.slice(0, 2).map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {item.tags?.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{item.tags.length - 2}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-2 hidden sm:table-cell text-stone-500 dark:text-stone-400">
                    {formatFileSize(item.file_size_bytes)}
                  </td>
                  <td className="p-2 hidden lg:table-cell text-stone-500 dark:text-stone-400">
                    {formatDate(item.created_at)}
                  </td>
                  <td className="p-2 text-center">
                    {item.in_gallery ? (
                      <Eye className="h-4 w-4 text-green-600 mx-auto" title="In Gallery Editor" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-stone-400 mx-auto" title="Not in Gallery Editor" />
                    )}
                  </td>
                  <td className="p-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditItem(item)}>
                          <Info className="h-4 w-4 mr-2" />
                          Edit Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleGallery(item)}>
                          {item.in_gallery ? (
                            <>
                              <EyeOff className="h-4 w-4 mr-2" />
                              Remove from Gallery
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-2" />
                              Add to Gallery
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(item)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <span className="text-sm text-stone-500 dark:text-stone-400 px-4">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Image Details</DialogTitle>
          </DialogHeader>
          {editItem && (
            <div className="space-y-4">
              {/* Large Image Preview */}
              <div className="w-full bg-stone-50 rounded-lg overflow-hidden border">
                <img
                  src={editItem.media_url}
                  alt={editItem.title}
                  className="w-full h-auto max-h-[50vh] object-contain bg-stone-100"
                />
              </div>

              {/* Metadata Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left Column - Editable Fields */}
                <div className="space-y-3">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={editItem.title}
                      onChange={(e) => setEditItem({ ...editItem, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={editItem.description || ''}
                      onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
                      placeholder="Optional description..."
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="in_gallery"
                        checked={editItem.in_gallery}
                        onCheckedChange={(checked) => setEditItem({ ...editItem, in_gallery: checked })}
                      />
                      <Label htmlFor="in_gallery">Add to Gallery Editor</Label>
                    </div>
                    <p className="text-xs text-stone-500 dark:text-stone-400 ml-6">
                      When checked, this image can be managed in the Gallery Editor for public display
                    </p>
                  </div>
                </div>

                {/* Right Column - Read-only Info */}
                <div className="space-y-3">
                  {editItem.tags?.length > 0 && (
                    <div>
                      <Label className="text-sm text-stone-500 dark:text-stone-400">Tags</Label>
                      <div className="flex gap-1 flex-wrap mt-1">
                        {editItem.tags.map(tag => (
                          <Badge key={tag} variant="outline">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-500 dark:text-stone-400">Filename:</span>
                      <span className="font-medium text-right max-w-[60%] truncate" title={editItem.original_filename}>
                        {editItem.original_filename || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500 dark:text-stone-400">File Size:</span>
                      <span className="font-medium">{formatFileSize(editItem.file_size_bytes)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500 dark:text-stone-400">Date Added:</span>
                      <span className="font-medium">{formatDate(editItem.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500 dark:text-stone-400">Image ID:</span>
                      <span className="font-mono text-xs text-stone-400">{editItem.id}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={handleUpdateItem}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage Warning Dialog */}
      <Dialog open={!!usageDialog} onOpenChange={() => setUsageDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Image In Use
            </DialogTitle>
          </DialogHeader>
          {usageDialog && (
            <div className="space-y-4">
              <p className="text-sm text-stone-600">
                This image is being used in {usageDialog.usages.length} location(s).
                Deleting it will break these references:
              </p>

              <div className="max-h-[200px] overflow-y-auto space-y-2">
                {usageDialog.usages.map((usage, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-stone-50 rounded text-sm">
                    <Badge variant="outline">{usage.collection}</Badge>
                    <span className="flex-1">{usage.display_name}</span>
                    <span className="text-xs text-stone-400">{usage.field}</span>
                  </div>
                ))}
              </div>

              <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded">
                Force deleting will remove the image but leave broken references.
                Consider updating these locations first.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUsageDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleForceDelete}>Force Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Tag Dialog */}
      <Dialog open={bulkTagDialog} onOpenChange={setBulkTagDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Tags for {selectedItems.size} Items</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                value={newTags}
                onChange={(e) => setNewTags(e.target.value)}
                placeholder="e.g., programs, hero, featured"
              />
            </div>
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={() => handleBulkTagUpdate('add')}
                disabled={!newTags.trim()}
              >
                Add Tags
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => handleBulkTagUpdate('remove')}
                disabled={!newTags.trim()}
              >
                Remove Tags
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmProps} />
    </div>
  );
};

export default MediaLibraryManager;
